import * as DsIcon from "@equipmentshare/ds-icon";
import { Col as AntCol, notification, Row as AntRow } from "antd";
import { Option, pipe } from "fp";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import styled from "styled-components";

import { Branch } from "~/Branch";
import * as Button from "~/Button";
import * as Color from "~/Color";
import * as Driver from "~/Driver";
import { Form } from "~/Form";
import * as Layout from "~/Layout";
import * as Log from "~/Log";
import { Remote } from "~/Remote";
import { Time } from "~/Time";
import * as Typography from "~/Typography";
import { WebAPI } from "~/WebAPI";
import { WorkOrder } from "~/WorkOrder";
import { DriverTimezoneBanner } from "../../DriverTimezoneBanner";

import { RevisionStatusWarningFlyout } from "./RevisionStatusWarningFlyout";

import * as Fields from "./Fields";
import { State } from "./State";
import { EndTimePicker, StartTimePicker } from "./TimePickers";

export { Drawer } from "./Drawer";

type SuggestedRevision = {
  readonly revision: Log.Event.Event | Log.Suggestion.Suggestion;
};

export type Props = SuggestedRevision &
  Partial<Branch> & {
    readonly route: Log.Route;
    readonly onClose: () => void;
  };

export const Label = Typography.H4.Component;

export const Component = ({ branch, route, revision, onClose }: Props) => {
  const [fieldErrors, setFieldErrors] = useState<
    Form.Field.ValidationErrors<Fields.Fields>
  >(Fields.Validation.defaultErrors());

  const driverZone = Driver.useFromReference({
    ...route.driver,
    missing: () => undefined,
    found: ({ time }) => time,
  });

  const statusValue = revision?.status ?? Driver.Status.on();

  const [workingRevision, updateWorkingRevision] = useState<State>({
    driverStatus: statusValue,
    startTime: Time.DateTime.fromNumber(
      (revision?.interval.start ?? Time.DateTime.now()).dateTime
    ),
    endTime: Time.DateTime.fromNumber(
      pipe(
        revision ?? Time.Interval.fromDateTimes(Time.DateTime.now())(),
        ({ interval }) =>
          Time.Interval.when({
            started: (started) => started.interval.start,
            stopped: (stopped) => stopped.interval.stop,
          })({ interval })
      ).dateTime
    ),
    location: revision?.location ?? "",
    workOrder: "workOrder" in revision ? revision?.workOrder : undefined,
    workCodes: "workCodes" in revision ? revision?.workCodes : undefined,
    job: "job" in revision ? revision?.job : undefined,
    notes: "",
  });

  const canEditTime = pipe(
    Option.fromNullable(revision),
    Option.fold(
      () => false,
      (revision) =>
        revision?.status && revision.sources && !Log.Event.canEditHOS(revision)
    )
  );

  const allowedStatuses = canEditTime
    ? [
        Driver.Status.drive(),
        Driver.Status.yardMove(),
        Driver.Status.personalUse(),
      ]
    : Driver.Status.toList();

  const updateForm = (revision: State, data: Partial<State>) => {
    updateWorkingRevision({
      ...revision,
      ...data,
    });
  };

  const updateFormAndValidate = (
    field: Fields.Fields,
    revision: State,
    data: Partial<State>
  ) => {
    updateForm(revision, data);
    void Fields.Validation.validateField(
      field,
      data,
      fieldErrors,
      setFieldErrors
    );
  };
  
  const timeZone = driverZone?.zone;
  const startDateInDriverTz = timeZone ? moment(route.dateTime).tz(timeZone, true) : undefined;
  const startDateIsoString = startDateInDriverTz ? startDateInDriverTz.toISOString() : undefined;

  const queryClient = useQueryClient();

  const addRevision = Log.Revision.Request.Post.Update.use();

  useEffect(
    () =>
      Remote.when(addRevision.response, {
        idle: () => {},
        loading: () => {},
        error: (error) => {
          const isDisplayable = WebAPI.ServiceError.isDisplayableError(error);
          void notification.error({
            message: "Error",
            description: isDisplayable
              ? error.message
              : "Unable to create a revision suggestion.",
            duration: isDisplayable ? 0 : 6,
            icon: (
              <DsIcon.Icon name="$icon_alert_fill" color="$color_primary_red" />
            ),
            placement: "topRight",
          });
          // A displayable error means the input needs fixing — keep the modal
          // open so the user doesn't lose their edits (see SC-508345's follow-up
          // MR description). Any other error closes it as before.
          if (!isDisplayable) onClose();
        },
        success: () => {
          void queryClient.invalidateQueries(
            Log.Revision.Request.Get.Query.key(route)
          );
          void notification.success({
            message: "Success",
            description: "Revision suggestion successfully created",
            duration: 4,
            icon: (
              <DsIcon.Icon
                name="$icon_check_circle_fill"
                color="$color_primary_green"
              />
            ),
            placement: "topRight",
          });
          onClose();
        },
      }),
    [addRevision.response]
  );

  const submitForm = (data: Partial<State>, driverZone: Time.Zone) => {
    if (Fields.Validation.validateForm(data, fieldErrors, setFieldErrors)) {
      addRevision.request({
        driver: route.driver,
        suggestion: State.toSuggestion(workingRevision),
        timezone: driverZone,
      });
    }
  };

  const formIsValid = Fields.Validation.isFormValid(workingRevision);

  return (
    <Container>
      {driverZone && (
        <TimezoneBannerContainer>
          <DriverTimezoneBanner driverTimezone={driverZone} />
        </TimezoneBannerContainer>
      )}
      <FormSpacer />
      {startDateIsoString && <RevisionStatusWarningFlyout 
        startDate={startDateIsoString} 
        driverId={route.driver.id}
      />}
      <form>
        <Fields.DriverStatus.Select
          status={workingRevision.driverStatus}
          allowedStatuses={allowedStatuses}
          onSelect={(driverStatus) =>
            updateForm(workingRevision, { driverStatus })
          }
        />
        <AntRow>
          <AntCol span={12} style={{ paddingBottom: "4px" }}>
            <Form.Field.Label.Vertical label="Start time">
              <StartTimePicker
                revision={workingRevision}
                onChange={(time) => {
                  if (time !== null) {
                    const oldDateWithNewTime = moment(
                      workingRevision.startTime.dateTime
                    )
                      .startOf("day")
                      .add(time.hour(), "hours")
                      .add(time.minute(), "minutes")
                      .add(time.seconds(), "seconds");
                    updateFormAndValidate("startTime", workingRevision, {
                      ...workingRevision,
                      startTime: Time.DateTime.fromNumber(
                        oldDateWithNewTime.valueOf()
                      ),
                    });
                  }
                }}
              />
            </Form.Field.Label.Vertical>
          </AntCol>
          <AntCol span={12} style={{ paddingBottom: "4px" }}>
            <Form.Field.Label.Vertical label="End time">
              <EndTimePicker
                revision={workingRevision}
                onChange={(time) => {
                  if (time !== null) {
                    const oldDateWithNewTime = moment(
                      workingRevision.endTime.dateTime
                    )
                      .startOf("day")
                      .add(time.hour(), "hours")
                      .add(time.minute(), "minutes")
                      .add(time.seconds(), "seconds");
                    updateFormAndValidate("endTime", workingRevision, {
                      ...workingRevision,
                      endTime: Time.DateTime.fromNumber(
                        oldDateWithNewTime.valueOf()
                      ),
                    });
                  }
                }}
              />
            </Form.Field.Label.Vertical>
          </AntCol>
          <Form.Field.Errors
            names={["startTime", "endTime"]}
            errors={fieldErrors}
          />
        </AntRow>
        <Fields.Location.Input
          location={workingRevision.location}
          errors={fieldErrors}
          onChange={(location) =>
            updateFormAndValidate("location", workingRevision, { location })
          }
        />
        <Fields.WorkOrder.Select
          disabled={workingRevision.driverStatus === Driver.Status.off()}
          branch={branch}
          workOrder={workingRevision.workOrder}
          revision={revision}
          onSelect={(workOrder) =>
            updateForm(workingRevision, {
              workOrder: workOrder
                ? WorkOrder.removeIDPrefix(workOrder)
                : undefined,
            })
          }
        />
        <Fields.Job.Select
          job={workingRevision.job}
          onChange={(job) => updateForm(workingRevision, { job })}
          disabled={workingRevision.driverStatus === Driver.Status.off()}
        />
        <Fields.WorkCodes.Select
          job={workingRevision.job}
          workCodes={workingRevision.workCodes}
          onSelect={(selected) =>
            updateForm(workingRevision, {
              workCodes: selected ? selected : undefined,
            })
          }
          disabled={workingRevision.driverStatus === Driver.Status.off()}
        />
        <Fields.Notes.Input
          notes={workingRevision.notes}
          errors={fieldErrors}
          onChange={(notes) =>
            updateFormAndValidate("notes", workingRevision, { notes })
          }
        />
        <Layout.Flex.Component all row justify="flex-end">
          <Layout.Flex.Component all>
            <Button.Component
              title="Cancel"
              icon="$icon_close_circle_line"
              secondary
              neutral
              onClick={onClose}
            />
          </Layout.Flex.Component>
          <Layout.Flex.Component all>
            <Button.Component
              title="Suggest"
              icon="$icon_add_circle_line"
              primary
              action
              disabled={!formIsValid}
              onClick={() =>
                submitForm(workingRevision, driverZone ?? Time.Zone.local())
              }
            />
          </Layout.Flex.Component>
        </Layout.Flex.Component>
      </form>
    </Container>
  );
};

export const Container = styled(Layout.Block.Component)`
  display: flex;
  flex-direction: column;
  justify-content: stretch;

  & .ant-form {
    width: 100%;
  }

  & .ant-input,
  & .ant-picker {
    min-height: 40px;
    border-radius: 4px;
  }

  & select {
    width: 100%;
    padding: 6px;
    min-height: 40px;
    border-radius: 4px;
    border-color: ${Color.veryWhiteGrey};
  }

  & input::-webkit-calendar-picker-indicator {
    color: ${Color.mist};
  }
`;

const TimezoneBannerContainer = styled.div`
  margin: -25px -25px -5px -25px;

  & p {
    padding: 10px;
  }
`;

const FormSpacer = styled.div`
  height: 5px;
`;
